import { useState, useEffect, memo, useRef } from 'react';
import { GatewayLogType } from '../../utils/types';
import { addLineBreaks } from '../../utils/functions';
import { ToolType } from '../../utils/types';
import '../../App.css';
import './logs.css';

// classes
import StatusIndicator from '../../tools/status-indicator';

//  ------------------------------------------------------------
//
//  Get the string name of a log level from its number.
//
//  ------------------------------------------------------------
const getLevelName = (level: number) => {
  switch (level) {
    case 50:
      return 'CRITICAL';
    case 40:
      return 'ERROR';
    case 30:
      return 'WARNING';
    case 20:
      return 'INFO';
    case 10:
      return 'DEBUG';
    case 0:
    default:
      return 'NOTSET';
  }
};

//  ------------------------------------------------------------
//
//  Get the background colour for a log level from its number.
//
//  ------------------------------------------------------------
const getLevelColor = (level: number) => {
  switch (level) {
    case 50:
      return 'black';
    case 40:
      return 'red';
    case 30:
      return 'orange';
    case 20:
      return 'blue';
    case 10:
      return 'green';
    case 0:
    default:
      return 'grey';
  }
};

//  ------------------------------------------------------------
//
//  Component for Level cell in log table row.
//
//  ------------------------------------------------------------
const LogLevelCell = (props: { level: number }) => {
  return (
    <td className="log-table-level">
      <StatusIndicator status={getLevelName(props.level).toUpperCase()} />
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for Date cell in log table row.
//
//  ------------------------------------------------------------
const LogDateCell = (props: { created_at: string }) => {
  const timestampParts = (() => {
    // Handle unparsable timestamp strings.
    try {
      return new Date(props.created_at).toLocaleString().split(', ');
    } catch (e) {
      console.log('Failed to parse log timestamp: ' + e);
      return [];
    }
  })();

  return (
    <td className="log-table-date">
      <div className="log-table-body-cell">
        {timestampParts.length === 2 ? (
          <>
            {timestampParts[0]}
            <br />
            {timestampParts[1]}
          </>
        ) : (
          '-'
        )}
      </div>
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for log Message cell in log table row.
//
//  ------------------------------------------------------------
const LogMessageCell = (props: { message: string }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    // Use a resize observer to determine if a log message cell contents are overflowing
    // and "show more/less" buttons should be used.
    const observer = new ResizeObserver(() => {
      if (container && !expanded)
        setIsOverflowing(container.scrollHeight > container.clientHeight);
    });

    if (container) observer.observe(container);

    return () => observer.disconnect();
  }, [expanded]);

  return (
    <td className="log-table-message">
      <div
        ref={containerRef}
        style={
          isOverflowing
            ? expanded
              ? {}
              : { maxHeight: '15pt', minHeight: '15pt', overflowY: 'hidden' }
            : { maxHeight: '30pt', minHeight: '30pt', overflowY: 'hidden' }
        }
      >
        {addLineBreaks(props.message)}
      </div>
      {isOverflowing && (
        <button
          className="log-message-expand-button"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? '▲' : '...▼'}
        </button>
      )}
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for log entry row in log table.
//
//  ------------------------------------------------------------
const LogTableRows = (props: { entries: GatewayLogType[] }) => {
  if (props.entries.length <= 0) return null;

  // Map a list of log entries to build table rows.
  return (
    <>
      {props.entries.map((entry: GatewayLogType, index: number) => {
        const reversedIndex = props.entries.length - 1 - index;
        const rowID = `log_table_row_${reversedIndex}`;
        return (
          <tr id={rowID} key={reversedIndex} tabIndex={0} className="log-table-row">
            <LogDateCell created_at={entry.created_at} />
            <td className="log-table-component">
              <div className="log-table-body-cell">{entry.component}</div>
            </td>
            <LogLevelCell level={entry.level} />
            <LogMessageCell message={entry.message} />
          </tr>
        );
      })}
    </>
  );
};

//  ------------------------------------------------------------
//
//  Component for log table.
//
//  ------------------------------------------------------------
export const Logs = memo(
  (props: {
    entries: GatewayLogType[];
    focusRow: React.MutableRefObject<string | undefined>;
  }) => {
    // Component is wrapped in a memo() so it only re-renders if its props change.
    const logTabRef = useRef<HTMLDivElement | null>(null);
    const isVisibleRef = useRef<boolean>(false);

    const handleVisibilityChange = (visible: boolean) => {
      if (visible) {
        // Check if there's a row to focus and focus it when switching to Logs tab.
        if (props.focusRow.current === undefined) return;

        const rowElement = document.getElementById(props.focusRow.current);
        if (rowElement === null) return;

        rowElement.focus({ preventScroll: false });
        return;
      }

      // Reset focusRow when switching to another tab
      props.focusRow.current = undefined;
    };

    const visibilityCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver,
    ) => {
      const currentlyVisible = entries[0].isIntersecting;

      // handle switches to and from the Logs tab.
      if (isVisibleRef.current !== currentlyVisible) {
        isVisibleRef.current = currentlyVisible;
        handleVisibilityChange(currentlyVisible);
      }
    };

    useEffect(() => {
      // Add observer to detect and set tab visibility.
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.0,
      };
      const visibilityObserver = new IntersectionObserver(visibilityCallback, options);

      if (logTabRef.current) {
        visibilityObserver.observe(logTabRef.current);
      }

      // Cleanup observer on component dismount.
      return () => visibilityObserver.disconnect();
    }, []);

    return (
      <div
        ref={logTabRef}
        style={{
          flex: '1 1',
          width: '100%',
          height: '100%',
          padding: '0px 5px 0px 0px',
        }}
      >
        <div className="panel-main" style={{ paddingTop: '0px' }}>
          <div className="log-table-body-scrollable-container">
            <table className="log-table">
              <thead className="log-table-head">
                <tr className="log-table-row text-colour">
                  <th className="log-table-date">Timestamp</th>
                  <th className="log-table-component">Component</th>
                  <th className="log-table-level">Level</th>
                  <th className="log-table-message">Message</th>
                </tr>
              </thead>
              <tbody className="log-table-body text-colour">
                <LogTableRows entries={props.entries} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  },
);
